/**
 * THE CUSTOMER REMINDER DRAFT QUEUE — a standalone destination, U2 / 5.6's web half.
 *
 * ===========================================================================
 * THE GOVERNING RULE: DRAFTED, NOT AUTO-SENT. A HUMAN REVIEWS AND SENDS.
 * ===========================================================================
 * Owner ruling, and the reason is asymmetric: a wrong expiry emailed to a CUSTOMER is a
 * business embarrassment, where a wrong internal alert is noise. This screen exists to make
 * that review REAL, so everything below is arranged around one question — *do I want this
 * letter to go to this person?* The recipient's address is therefore not a detail in a
 * tooltip; it is on the face of the card AND inside the button's own label, so the click
 * cannot be made without having read who it goes to.
 *
 * ===========================================================================
 * ⚠ THE SEND IS ONLY EVER A CLICK. NEVER ON RENDER, NEVER ON HOVER.
 * ===========================================================================
 * There is no effect, no prefetch and no mouse handler that can reach `sendRenewalDraft`;
 * the mutation is called from `onClick` and nowhere else. `renewal-drafts.test.tsx` asserts
 * zero calls after render and after a hover over the control, because "we never auto-send"
 * is the whole authorisation for this feature and a claim of that weight needs a probe that
 * would fail if somebody added a convenience.
 *
 * ===========================================================================
 * ⚠ D5 — THE SEND CONTROL IS ABSENT WITHOUT `quotes.send`, NOT DISABLED
 * ===========================================================================
 * Reading the queue is `quotes.view`; sending is `quotes.view` AND `quotes.send`
 * (`@RequirePerm` is AND). Those are not two grades of the same key — the seeded matrix
 * gives ACCOUNTS `quotes.view` and NOT `quotes.send`, so that seat must SEE what is due
 * (they chase money) and must have no way to email the customer. For them the button is
 * ABSENT, which is the truth about their authority; a row of greyed-out controls reads as a
 * broken screen and invites a support call.
 *
 * THE DISTINCTION THE CODE KEEPS CAREFULLY, and it is the QuoteRecordPage doctrine:
 *   · a PERMISSION gate HIDES — no `quotes.send`, no button, anywhere on the screen;
 *   · a STATE gate SHOWS AND EXPLAINS — a superseded or re-dated draft keeps its button,
 *     disabled, with the reason in words, because the operator needs to learn WHY this one
 *     is not going out. Hiding it there would make a refusal indistinguishable from a
 *     missing permission.
 * A contrast pair pins exactly that: the no-send seat gets `queryBy… === null`, and the
 * superseded row gets a control that exists and is disabled.
 *
 * ===========================================================================
 * ⚠ "DRAFTED BUT NEVER SENT" IS THE WHOLE POINT, AND IT MUST NOT RENDER AS A BLANK
 * ===========================================================================
 * Rule 11, and the state migration 27 was authorised for. The API's queue predicate is
 * `audience = 'customer' AND state = 'drafted'`, so EVERY row here is by construction a
 * letter nobody has sent — and a successful send removes the row from the next read. Three
 * things follow, all of them deliberate:
 *
 *  1. Every card states its own status IN WORDS. `Not sent yet` is printed, never implied by
 *     an empty space, because an ambiguous blank is precisely what the ledger change was
 *     made to abolish.
 *  2. A draft whose last send FAILED says so — `Send failed` plus `send_error` verbatim —
 *     and keeps its place. The server's contract is that a refused send stays `drafted` with
 *     the reason on the row and answers 409 rather than a 200 with a sad field; this screen
 *     is the surface that makes that reason readable by a person.
 *  3. What DID go out is read back FROM THE SERVER. ⚠ RULE-7 CALLOUT — THIS POINT USED TO
 *     DESCRIBE A SESSION-ONLY PANEL FED BY THIS TAB'S OWN POST RESPONSES, AND THAT PANEL IS
 *     GONE. It was honest when it was written, because the queue predicate excludes `sent`
 *     rows and no history route existed; U2/5.6b adds one, and leaving a session-only list
 *     beside a real history would be two answers to one question, differing whenever a
 *     colleague sent something from another desk. The "Sent" view and the "recently sent"
 *     strip are both fed by `GET /countries/:cc/renewal-reminders/sent`, so a reload no
 *     longer empties them and somebody else's send is visible here.
 *
 *  4. ⚠ AND EVERY CARD ANSWERS "HAVE WE ALREADY EMAILED THIS?" WITHOUT A CLICK. The doubt
 *     occurs while looking at the card, so the answer is on the card — including, and
 *     especially, when the answer is NO. `same_window_sends` counts customer reminders
 *     already SENT for this contract at this window under a DIFFERENT expiry, which is the
 *     one situation that produces a duplicate: editing an expiry re-arms all three windows
 *     (migration 19), so the scan drafts a window whose letter is already in the customer's
 *     inbox. Under the routine 60 → 30 → 7 cadence it is 0, and the zero is stated IN
 *     WORDS — an absent sentence is not an answer.
 *
 * ===========================================================================
 * MASKING — WHAT EXPIRES AND WHEN, NEVER THE VALUE, INCLUDING IN THE LETTER
 * ===========================================================================
 * `line_total` arrives as the literal `'•••'` for a seat without `amounts.view`, and goes
 * through `formatMoney`, which asks `isMaskedAmount` BEFORE it parses — `Number('•••')` is
 * `NaN`, and a value rendering NaN (or worse, 0) would tell a manager a contract is
 * worthless. Everything else stays legible: that seat still sees WHO, WHAT, WHEN and the
 * address, which is the entire bargain.
 *
 * ⚠ AND THE PREVIEW RENDERS `mail.subject` AND `mail.body` AND NOTHING ELSE OFF THE ROW.
 * The interceptor masks FIELDS and cannot see inside a sentence, so the server's template
 * deliberately carries no amount. Composing the value into the preview HERE would reopen
 * that hole from the client side, with the mask still visibly working two lines above.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Send } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, EmptyState, SkeletonRows } from '../../components/ui';
import { NO_VALUE, formatMoney, isMaskedAmount } from '../inquiries/format';
import { daysLabel, formatDateOnly, formatSentAt } from './format';
import { cn } from '../../lib/cn';
import { PagerFooter } from './PagerFooter';
import { SentReminders } from './SentReminders';
import { ClosedReminders } from './ClosedReminders';
import { CustomerFilter } from './CustomerFilter';
import {
  draftsErrorText,
  fetchClosedReminders,
  fetchRenewalDrafts,
  fetchSentReminders,
  isPartyFilter,
  renewalDraftKeys,
  sendRenewalDraft,
} from './drafts-api';
import type { RenewalDraftRow } from './drafts-api';
// The drilldown chooser, owned by the register's wire file — see its header.
import { renewalDocumentPath } from './api';

const PER_PAGE = 25;
/** How many of the most recent sends the strip above the queue shows. */
const RECENT_LIMIT = 3;

/** The three answers this screen can give. Not three destinations — see `ViewToggle`. */
type View = 'queue' | 'sent' | 'closed';

export function RenewalDraftsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();

  /*
   * ⚠ THE KEY THE ROUTE ENFORCES, AND ONLY IT. `GET /countries/:cc/renewal-drafts` is
   * `@RequirePerm('quotes.view')`. Gating the SCREEN on `quotes.send` would hide the queue
   * from Accounts — a seat the API serves — which is the Work Board defect this project has
   * now met five times. The send key gates the CONTROL, one level down, and nothing else.
   */
  const canView = can(countryCode, 'quotes.view');
  const canSend = canView && can(countryCode, 'quotes.send');

  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  /*
   * ⚠ THE MODE AND THE PARTY FILTER LIVE IN THE URL, NOT IN STATE. A card links to "what
   * has already gone to THIS customer", and that link has to survive a reload and be
   * pasteable into a message to a colleague — the same reason `?page` is in the URL.
   *
   * `isPartyFilter` is the single definition of what counts as a party id; anything else is
   * not one, so the list is NOT claimed to be filtered and the heading says so. Silently
   * dropping an unusable filter while still labelling the list "one customer" would show a
   * whole country's sends as one customer's, and an operator would stop chasing.
   */
  const viewParam = params.get('view');
  const view: View = viewParam === 'sent' ? 'sent' : viewParam === 'closed' ? 'closed' : 'queue';
  const partyParam = params.get('party');
  const partyFilter = isPartyFilter(partyParam) ? partyParam : null;

  /*
   * ⚠ THE CHOSEN CUSTOMER'S NAME, REMEMBERED FROM THE CLICK THAT SET THE FILTER — and NOT
   * derived from the id, because nothing on this screen can resolve one. After a reload or a
   * pasted link it is `null` and `CustomerFilter` says so in words rather than printing the
   * id as though it were a name.
   */
  const [partyName, setPartyName] = useState<string | null>(null);

  const filters = useMemo(() => ({ page, perPage: PER_PAGE }), [page]);
  const sentFilters = useMemo(
    () => ({ page, perPage: PER_PAGE, party: partyFilter ?? undefined }),
    [page, partyFilter],
  );
  const recentFilters = useMemo(() => ({ page: 1, perPage: RECENT_LIMIT }), []);

  const queue = useQuery({
    queryKey: renewalDraftKeys.queue(countryCode, filters),
    queryFn: ({ signal }) => fetchRenewalDrafts(countryCode, filters, signal),
    enabled: canView && view === 'queue',
  });

  const sentList = useQuery({
    queryKey: renewalDraftKeys.sent(countryCode, sentFilters),
    queryFn: ({ signal }) => fetchSentReminders(countryCode, sentFilters, signal),
    enabled: canView && view === 'sent',
  });

  /**
   * ⚠ THE THIRD ANSWER. `skipped`, `superseded` and `missed` are customer-audience rows the
   * scan writes on purpose, and until now neither the queue's `state = 'drafted'` predicate
   * nor the history's `state = 'sent'` could see any of them — so a window the system had
   * decided never to chase looked exactly like a window that was never due. See
   * `ClosedReminders.tsx` for the measurement.
   */
  const closedList = useQuery({
    queryKey: renewalDraftKeys.closed(countryCode, sentFilters),
    queryFn: ({ signal }) => fetchClosedReminders(countryCode, sentFilters, signal),
    enabled: canView && view === 'closed',
  });

  /**
   * ⚠ THE STRIP ABOVE THE QUEUE, AND IT IS A SERVER READ RATHER THAN THIS TAB'S MEMORY.
   * Same route, three newest rows. It replaces the session-only panel outright — see the
   * file header, point 3 — so there is exactly one answer to "what has gone out", it
   * survives a reload, and a colleague's send appears in it.
   */
  const recent = useQuery({
    queryKey: renewalDraftKeys.sent(countryCode, recentFilters),
    queryFn: ({ signal }) => fetchSentReminders(countryCode, recentFilters, signal),
    enabled: canView && view === 'queue',
  });

  const [failedId, setFailedId] = useState<string | null>(null);
  const client = useQueryClient();

  /*
   * BOTH BRANCHES ARE INVALIDATED EXPLICITLY. The sent key is deliberately not nested under
   * `'drafts'`, so a prefix invalidation of the queue would not reach it — the refresh of
   * the history is a decision here rather than a side-effect of how a key was named.
   */
  const refreshBoth = (): void => {
    void client.invalidateQueries({ queryKey: ['renewals', countryCode, 'drafts'] });
    void client.invalidateQueries({ queryKey: ['renewals', countryCode, 'sent'] });
    /*
     * ⚠ THE CLOSED BRANCH TOO — it is not swept by either prefix above and a send CAN move a
     * row into it indirectly: the next scan may close the remaining windows as `superseded`.
     * Leaving it stale would show a decision that has since been overtaken.
     */
    void client.invalidateQueries({ queryKey: ['renewals', countryCode, 'closed'] });
  };

  const send = useMutation({
    mutationFn: (draftId: string) => sendRenewalDraft(countryCode, draftId),
    onSuccess: () => {
      setFailedId(null);
      /*
       * The row is now `sent`: the queue predicate excludes it and the history predicate
       * includes it, so BOTH lists genuinely move. Invalidated rather than patched locally
       * — each list's `total` is the honest count and must come back from the server.
       */
      refreshBoth();
    },
    onError: (_error, draftId) => {
      /*
       * A refused send leaves the row `drafted` WITH `send_error` written on it, so the
       * refetch is what makes the reason durable rather than a message that scrolls away.
       * `failedId` only marks which card to point at. The history is refetched too, and
       * that is the point: it must NOT have gained a row.
       */
      setFailedId(draftId);
      refreshBoth();
    },
  });

  const goToPage = (next: number): void => {
    const patched = new URLSearchParams(params);
    if (next <= 1) patched.delete('page');
    else patched.set('page', String(next));
    setParams(patched);
  };

  /** Switching view always returns to page 1 — page 4 of the queue is not page 4 of history. */
  const goToView = (next: View, party?: string, name?: string): void => {
    const patched = new URLSearchParams(params);
    patched.delete('page');
    if (next === 'queue') patched.delete('view');
    else patched.set('view', next);
    if (party === undefined) patched.delete('party');
    else patched.set('party', party);
    setPartyName(party === undefined ? null : (name ?? null));
    setParams(patched);
  };

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Renewal reminders are not available to this account">
          Viewing the reminder queue in {countryCode} needs the quotes permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = queue.data?.data ?? [];
  const total = queue.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Reminder drafts"
        subtitle={
          view === 'sent'
            ? sentList.data === undefined
              ? undefined
              : partyFilter === null
                ? `${String(sentList.data.total)} customer ${sentList.data.total === 1 ? 'reminder has' : 'reminders have'} been sent in ${countryCode}`
                : `${String(sentList.data.total)} sent to this customer`
            : view === 'closed'
              ? closedList.data === undefined
                ? undefined
                : partyFilter === null
                  ? `${String(closedList.data.total)} ${closedList.data.total === 1 ? 'window' : 'windows'} closed without a letter in ${countryCode}`
                  : `${String(closedList.data.total)} closed without a letter for this customer`
              : queue.data === undefined
                ? undefined
                : `${String(total)} awaiting review — ${total === 1 ? 'nothing here has' : 'none of these have'} been sent`
        }
      />

      <ViewToggle view={view} onView={goToView} />

      {/*
        ⚠ THE CUSTOMER FILTER IS ON BOTH HISTORY VIEWS AND NOT ON THE QUEUE, WHICH IS THE
        FIX FOR A CONFIRMED FINDING. Narrowing to a customer used to be reachable ONLY from a
        draft card's "What has been sent to X?" button — and the moment you finish sending,
        that customer has no draft and therefore no card, which is exactly when somebody asks
        whether they were chased. The queue keeps no filter because a queue empties: its rows
        ARE the customers with outstanding work.
      */}
      {view === 'queue' ? null : (
        <CustomerFilter
          countryCode={countryCode}
          partyFilter={partyFilter}
          partyName={partyName}
          onSelect={(id, name) => goToView(view, id, name)}
          onClear={() => goToView(view)}
        />
      )}

      {view === 'queue' ? (
        /*
          THE STANDING SENTENCE, AND IT IS NOT DECORATION. Somebody opening this screen for
          the first time has to know that nothing in the QUEUE has left the building, and
          that pressing Send is what puts a letter in a customer's inbox. Both facts are
          load-bearing and neither is inferable from a list of cards.
        */
        <p className="text-body13 text-fg-secondary" data-testid="queue-preamble">
          Every reminder below has been DRAFTED and NOT SENT. Nothing is emailed to a customer
          automatically — read the letter and the address, then send it.
        </p>
      ) : view === 'sent' ? (
        /*
          ⚠ AND THE MIRROR SENTENCE FOR THE OTHER TAB, WHICH IS THE ONE THAT PREVENTS THE
          MISREADING. Every row here IS in a customer's inbox. It says so, and it says the
          two things this list cannot tell you — the letter itself is not stored, and the
          expiry shown is the one the letter stated rather than the contract's expiry now.
        */
        <p className="text-body13 text-fg-secondary" data-testid="sent-preamble">
          Every reminder below WAS EMAILED to the customer, by somebody in {countryCode} — not
          only from this browser. The wording is not stored, so it is not shown; the expiry is
          the one that letter stated, which may not be the contract&rsquo;s expiry today.
        </p>
      ) : (
        /*
          ⚠ THE THIRD SENTENCE, AND IT IS THE ONE THAT STOPS THIS LIST BEING READ AS EITHER
          OF THE OTHER TWO. Nothing here was emailed and nothing here is waiting: each row is
          a window the system CLOSED, with the reason it closed. Saying that plainly is the
          whole reason the view exists — two empty lists were being read as "not due".
        */
        <p className="text-body13 text-fg-secondary" data-testid="closed-preamble">
          NOTHING BELOW WAS EMAILED, and nothing below is waiting for you. Each row is a
          reminder window the system closed on its own, with the reason. They are here so a
          silence has an explanation instead of looking like a customer who was never due.
        </p>
      )}

      {/*
        D5's other half: the control is absent for this seat, so the screen SAYS why rather
        than leaving a reviewer hunting for a button that was never rendered. It names no
        permission key — the house rule — and it is a sentence, not a disabled control.
      */}
      {!canSend && view === 'queue' ? (
        <Alert tone="info" title="This account can review the queue but not send">
          You can see what is due and read exactly what would go out. Sending a reminder to a
          customer is done by a colleague whose access includes it.
        </Alert>
      ) : null}

      {view === 'sent' ? (
        <SentReminders
          countryCode={countryCode}
          page={sentList.data}
          isPending={sentList.isPending}
          isError={sentList.isError}
          error={sentList.error}
          onRetry={() => {
            void sentList.refetch();
          }}
          onPage={goToPage}
          partyFilter={partyFilter}
          onClearParty={() => goToView('sent')}
        />
      ) : view === 'closed' ? (
        <ClosedReminders
          countryCode={countryCode}
          page={closedList.data}
          isPending={closedList.isPending}
          isError={closedList.isError}
          error={closedList.error}
          onRetry={() => {
            void closedList.refetch();
          }}
          onPage={goToPage}
          partyFilter={partyFilter}
          onClearParty={() => goToView('closed')}
        />
      ) : (
        <>
          {/*
            ⚠ A FAILED READ IS NOT AN EMPTY HISTORY, AND IT USED TO RENDER AS ONE. The strip
            was `recent.data !== undefined && recent.data.total > 0`, so a 500 or a 403 on the
            sent route produced a screen BYTE-IDENTICAL to "nobody has ever been emailed" — a
            reviewer compared `document.body.textContent` for the two and they were the same
            string. `PriorSends`' own docblock states the rule that broke: "a reader cannot
            tell 'we have not contacted them' from 'this did not load', and the one they act
            on is the negative." The `?view=sent` tab had an error Alert; this strip had none.

            It is a quiet line rather than a danger Alert because the queue below IS loaded
            and usable — the failure is one strip's, and shouting would misdescribe the page.
          */}
          {recent.isError ? (
            <p
              data-testid="recently-sent-error"
              className="rounded-md border border-line bg-warn-bg px-4 py-3 text-body13 text-warn-fg"
            >
              <span className="font-medium">
                What has already been sent could not be loaded.
              </span>{' '}
              This is not the same as nothing having been sent — treat it as unknown.{' '}
              <button
                type="button"
                data-testid="recently-sent-retry"
                onClick={() => {
                  void recent.refetch();
                }}
                className="font-medium underline underline-offset-2"
              >
                Try again.
              </button>
            </p>
          ) : recent.data !== undefined && recent.data.total > 0 ? (
            <RecentlySentPanel
              page={recent.data}
              onSeeAll={() => goToView('sent')}
            />
          ) : null}

          {queue.isPending ? (
            <div className="rounded-md border border-line bg-surface px-4">
              <SkeletonRows rows={4} label="Loading reminder drafts" />
            </div>
          ) : queue.isError ? (
            <Alert tone="danger" title="The reminder queue could not be loaded">
              <p>{draftsErrorText(queue.error)}</p>
              <button
                type="button"
                onClick={() => {
                  void queue.refetch();
                }}
                className="mt-1 font-medium underline underline-offset-2"
              >
                Try again.
              </button>
            </Alert>
          ) : rows.length === 0 && total > 0 ? (
            /*
             * AN EMPTY PAGE IS NOT AN EMPTY QUEUE. Conflating them here would tell somebody
             * "there is nothing to send" while a queue of unsent customer letters sat one
             * page back — U5.5's own defect, on a screen where the consequence is a customer
             * nobody chased.
             */
            <Alert
              tone="info"
              title={`Page ${String(queue.data.page)} is past the end of this queue`}
            >
              <p>
                {String(total)} {total === 1 ? 'reminder is' : 'reminders are'} waiting to be
                sent.
              </p>
              <button
                type="button"
                onClick={() => goToPage(1)}
                className="mt-1 font-medium underline underline-offset-2"
              >
                Show the first page.
              </button>
            </Alert>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed border-line bg-surface">
              {/*
                ⚠ THE EMPTY STATE MUST NOT READ AS "NOTHING IS DUE". It means no letter is
                AWAITING A HUMAN — the contracts themselves are on the renewals register, and
                the description says so and links there. An empty queue and an empty pipeline
                are different facts, and only one of them is good news.
              */}
              <EmptyState
                title="No customer reminder is waiting to be sent."
                description="This queue holds drafts nobody has sent yet. Contracts that are due appear on the renewals register whether or not a reminder has been drafted for them."
                action={
                  <Link
                    to={`/countries/${countryCode}/renewals`}
                    className="text-body13 font-medium text-indigo hover:underline"
                  >
                    Open the renewals register.
                  </Link>
                }
              />
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {rows.map((row) => (
                <li key={row.id}>
                  <DraftCard
                    countryCode={countryCode}
                    row={row}
                    canSend={canSend}
                    sending={send.isPending && send.variables === row.id}
                    sendError={
                      failedId === row.id && send.isError ? draftsErrorText(send.error) : null
                    }
                    onSend={() => send.mutate(row.id)}
                    onSeeSent={() => goToView('sent', row.party_id, row.party_name)}
                  />
                </li>
              ))}
            </ul>
          )}

          {queue.data !== undefined && rows.length > 0 ? (
            <PagerFooter
              page={queue.data.page}
              perPage={queue.data.perPage}
              shown={rows.length}
              total={queue.data.total}
              onPage={goToPage}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────── the two-view toggle ── */

/**
 * ⚠ ONE SCREEN, THREE VIEWS — NOT THREE NAV ROWS, AND THAT IS DELIBERATE.
 *
 * The operator's question ("have we already chased this customer?") arises while looking at
 * a draft card. A separate destination means leaving the card, and somebody who has to
 * navigate to find out often does not. Making the histories MODES of this screen also means
 * no new nav entry, which is one fewer place to gate a row on a permission its own route
 * does not require — the defect this project has met five times. `nav.test.ts` pins the
 * Renewals section at exactly two rows so this decision gets re-made rather than drifted into.
 *
 * All three views are served by routes carrying the SAME key, `quotes.view`, so the toggle
 * needs no gate of its own: any seat that can see this screen can see all three of them.
 *
 * ⚠ THE THIRD BUTTON IS NEW AND IT CLOSES A CONFIRMED FINDING. Two of the ledger's five
 * customer states — three, in fact — had no surface anywhere, so an operator saw an empty
 * queue and an empty history and read that as "not due" when the system had decided never to
 * chase this customer and had recorded why.
 *
 * ⚠ THE PARTY CHIP MOVED OUT OF HERE INTO `CustomerFilter`, which both narrows AND says what
 * it is narrowed to. A chip that can only be REMOVED was half an affordance: it could clear
 * a filter set from a draft card and could never set one, so a customer with no draft could
 * not be looked up at all.
 */
function ViewToggle({
  view,
  onView,
}: {
  view: View;
  onView: (next: View) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="view-toggle">
      <div className="inline-flex rounded-md border border-line bg-surface p-0.5">
        <ToggleButton active={view === 'queue'} onClick={() => onView('queue')} testId="view-queue">
          Awaiting review
        </ToggleButton>
        <ToggleButton active={view === 'sent'} onClick={() => onView('sent')} testId="view-sent">
          Already sent
        </ToggleButton>
        <ToggleButton
          active={view === 'closed'}
          onClick={() => onView('closed')}
          testId="view-closed"
        >
          Closed without a letter
        </ToggleButton>
      </div>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-sm px-3 py-1 text-body13',
        // `text-fg` on `bg-subtle` — 16.14:1 light / 15.11:1 dark. The dimmer tokens both
        // fail AA on that tint (secondary 4.40, muted 2.49); quiet is the tint's job.
        active ? 'bg-subtle font-medium text-fg' : 'text-fg-secondary hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}

/* ──────────────────────────────────────────────────── what went out today ── */

/**
 * ⚠ THIS REPLACED A SESSION-ONLY PANEL, AND THE REPLACEMENT IS THE WHOLE POINT (RULE 7).
 *
 * The old `SentThisSessionPanel` listed the responses to THIS TAB's own POSTs and said so
 * honestly, because the queue route returns `state = 'drafted'` rows exclusively and no
 * history route existed. One now does. Keeping both would have left two answers to one
 * question — differing the moment a colleague sent something from another desk, and with the
 * screen offering no way to tell which was right.
 *
 * So this reads the SERVER: the three most recent sends in the country, from the same route
 * the "Already sent" view uses. It survives a reload, it includes other people's work, and
 * its count is the server's. The honesty statement changed with the mechanism: it no longer
 * says "this browser only", because that is no longer true, and it does say the list is the
 * newest few rather than all of them.
 */
function RecentlySentPanel({
  page,
  onSeeAll,
}: {
  page: { data: readonly { id: string; recipient_email: string | null; sent_at: string | null }[]; total: number };
  onSeeAll: () => void;
}): React.JSX.Element {
  const shown = page.data.length;
  return (
    <div data-testid="recently-sent" className="rounded-md border border-line bg-ok-bg px-4 py-3">
      <p className="text-body13 font-medium text-ok-fg">
        {page.total === 1
          ? '1 customer reminder has been sent'
          : `${String(page.total)} customer reminders have been sent`}
      </p>
      <ul className="mt-1 flex flex-col gap-0.5">
        {page.data.map((row) => (
          <li key={row.id} className="text-label text-ok-fg">
            {formatSentAt(row.sent_at)} — {row.recipient_email ?? NO_VALUE}
          </li>
        ))}
      </ul>
      {/*
        ⚠ THE HONEST COUNT, AGAIN, AND IT IS THE SAME RULE AS `PagerFooter`'s. This strip
        shows at most three rows; if the server's total is larger it SAYS the list is
        partial, because a list of three under a heading of twelve reads as "and that is all
        of them" — the U5.5 defect in miniature.
      */}
      <button
        type="button"
        data-testid="see-all-sent"
        onClick={onSeeAll}
        className="mt-1 text-label font-medium text-ok-fg underline underline-offset-2"
      >
        {page.total > shown
          ? `Showing the ${String(shown)} most recent — see all ${String(page.total)}.`
          : 'See the full list.'}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── the card ── */

/**
 * ⚠ A CARD, NOT A TABLE ROW, AND THAT IS THE FEATURE'S SHAPE RATHER THAN A PREFERENCE.
 * The register (U5.5) is a table because every one of its columns is a short scalar. This
 * queue's central content is a multi-line LETTER plus a recipient plus a refusal reason; a
 * table forcing those into cells would either truncate the body — hiding the thing the
 * reviewer is here to read — or produce a 1180px row that scrolls the address off screen,
 * which is the one field the whole human step exists for.
 */
function DraftCard({
  countryCode,
  row,
  canSend,
  sending,
  sendError,
  onSend,
  onSeeSent,
}: {
  countryCode: string;
  row: RenewalDraftRow;
  canSend: boolean;
  sending: boolean;
  sendError: string | null;
  onSend: () => void;
  onSeeSent: () => void;
}): React.JSX.Element {
  /*
   * The THREE STATE blocks. Each is refused by the server with a status that explains
   * itself; the screen states them BEFORE the click because the queue is a list somebody
   * reads at 09:00 and acts on at 11:00, and the world moves in between.
   *
   * ⚠ THE THIRD IS THE SIGN-OFF GATE, AND IT IS HERE FOR THE SIXTH INSTANCE OF THE WORK
   * BOARD DEFECT: gate the control on what the ROUTE requires. `POST …/send` refuses a
   * country whose `legal_entity` is a placeholder or spans more than one line (422
   * `LEGAL_ENTITY_UNUSABLE`), so leaving this out would render a fully enabled "Send to
   * <address>" button for a letter the server will not post. And this client CANNOT
   * derive it — every company name on screen comes from the hardcoded `COUNTRIES` mirror,
   * never from the API — which is why the row carries the answer.
   */
  const blocked = row.superseded || row.stale || row.legal_entity_defect !== null;

  // `null` = this build has no screen for that document type. See the drilldown below.
  const documentPath = renewalDocumentPath(countryCode, row.document_type, row.document_id);

  return (
    <article
      data-testid="draft-card"
      data-draft-id={row.id}
      data-source={row.source}
      className="flex flex-col gap-3 rounded-md border border-line bg-surface p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/countries/${countryCode}/parties/${row.party_id}`}
              className="text-body font-medium text-indigo hover:underline"
            >
              {row.party_name}
            </Link>
            <WindowPill days={row.window_days} />
          </div>
          <p className="text-body13 text-fg">{row.product_name}</p>
        </div>

        <div className="flex flex-col items-end gap-1">
          <StatusChip row={row} />
          {/*
            THE DRILLDOWN FOLLOWS THE SOURCE. A Tally Sales subscription's originating
            document is a QUOTE, not a sales order (U5.4) — linking every card to
            /sales-orders would give half of them a dead link, which is worse than no link
            because the operator concludes the document is missing.

            ⚠ CHOSEN BY `renewalDocumentPath`, NOT BY A TERNARY whose `else` arm any
            non-sales-order type could reach — see that function on the dead link a third
            document type would have produced here. `null` means this build has no screen
            for the type, and the doc number renders as plain text rather than as a link
            somewhere wrong.
          */}
          {documentPath === null ? (
            <span
              className="text-body13 font-medium text-fg"
              title="This build does not know which screen shows this document."
            >
              {row.document_doc_no}
            </span>
          ) : (
            <Link to={documentPath} className="text-body13 font-medium text-indigo hover:underline">
              {row.document_doc_no}
            </Link>
          )}
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-body13">
        <div className="flex gap-2">
          <dt className="text-fg-secondary">Expires</dt>
          <dd className="tnum text-fg">
            {formatDateOnly(row.expiry_date)}{' '}
            <span className="text-fg-secondary">({daysLabel(row.days_remaining)})</span>
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-fg-secondary">Value</dt>
          {/*
            The masked-cell idiom from the register's `MoneyCell` — the value is NEVER
            parsed, and the mask carries its REASON in a `title` rather than being a bare
            marker.

            ⚠ RULE-5 CALLOUT — THIS COMMENT USED TO SAY "ONE DELIBERATE DEPARTURE" AND THE
            DEPARTURE IS OVER. When this screen was built the register rendered the same
            marker in `text-fg-muted` — **2.63:1** on the surface in light mode, a token the
            contrast suite classifies as DECORATION and pins under 3:1 — and this unit chose
            `text-fg-secondary` instead, FILING the register rather than reaching into a
            shipped U5.5 screen (rule 6). The owner has since authorised the fix: all seven
            masked-marker sites in the app now wear `text-fg-secondary`, so this line is the
            HOUSE RECIPE, not a local exception. Saying otherwise would be a stale comment
            asserting a live defect that no longer exists.

            The measurement is unchanged and is why the token is what it is: `•••` is not
            decoration — it is the cell's entire content and the only thing telling that
            seat the figure exists and is withheld. `text-fg-secondary` measures 4.64 light
            / 5.85 dark on this card, quieter than the `text-fg` an unmasked figure gets, so
            the distinction survives while clearing the floor in both modes.

            ⚠ STILL OPEN, AND NOT THIS CELL'S TO CLOSE: on the register's HOVERED row the
            same pair sits on `bg/subtle` at 4.40:1. This card never hovers, so it is not a
            condition here — it is reported against the register.
          */}
          <dd
            data-testid="draft-value"
            className={cn('tnum', isMaskedAmount(row.line_total) ? 'text-fg-secondary' : 'text-fg')}
            title={
              isMaskedAmount(row.line_total)
                ? 'Hidden — this account cannot view amounts in this country'
                : undefined
            }
          >
            {formatMoney(row.line_total, row.currency)}
          </dd>
        </div>
      </dl>

      {/*
        ⚠ THE RECIPIENT, PLAINLY, AND IT IS THE POINT OF THE HUMAN STEP.
        A reviewer must be able to see WHO this is about to go to before sending. It is given
        its own labelled block rather than a line of metadata, and the address is `text-fg`
        at body size — not a muted caption — because the failure this screen prevents is a
        letter going to the wrong person.

        ⚠ EVERY WORD IN THIS BLOCK IS `text-fg`, AND THAT IS MEASURED RATHER THAN CHOSEN.
        The block sits on `bg/subtle`, where the app's two dimmer text tokens both fail AA:
        `text/secondary` is **4.40:1** in light mode (the repo has now refused that pair in
        four separate units) and `text/muted` is **2.49:1**, the worst text pair in the
        app. QUIET IS A PROPERTY OF THE BACKGROUND — the tint is what separates this block
        from the card, and dimming the type on top of it would only make the one field the
        human step exists for the hardest thing on the card to read. Hierarchy is carried
        by SIZE and WEIGHT instead, which survives greyscale as colour does not.
      */}
      <div
        data-testid="draft-recipient"
        className="flex flex-wrap items-baseline gap-x-2 rounded-sm bg-subtle px-3 py-2"
      >
        <span className="text-label uppercase text-fg">To</span>
        <span className="text-body font-medium text-fg">{row.recipient.email}</span>
        <span className="text-body13 text-fg">{row.recipient.name ?? NO_VALUE}</span>
      </div>

      <PriorSends row={row} onSeeSent={onSeeSent} />

      {/*
        ⚠ THE PREVIEW RENDERS `mail.subject` AND `mail.body`. NOTHING ELSE OFF THE ROW.
        See the file header: the amounts interceptor masks fields and cannot see inside a
        sentence, so the server's template carries no figure — and pasting one in here would
        reopen that hole from the client side. The test asserts this block's text is exactly
        subject + body, which is a claim that fails the moment anything else is composed in.
      */}
      <section aria-label="The drafted message" className="rounded-sm border border-line">
        {/*
          ⚠ `text-fg-secondary`, NOT `text-fg-muted`, AND IT IS THE SAME MEASUREMENT AS THE
          RECIPIENT BLOCK. `text/muted` on the card surface is 2.63:1 — the token set's own
          contrast suite records it as DECORATION rather than text, and pins it under 3:1.
          This line is a real instruction to a human about to email a customer, so it takes
          the dimmest token that still clears AA: `text/secondary`, 4.64 light / 5.85 dark.

          ⚠ RULE-5 CALLOUT: when this was written the same reasoning had NOT been applied to
          the register, and this comment read as a local exception. It is now the app-wide
          rule — `text/muted` carries no text on any surface in this feature, and the seven
          masked-marker sites that used to disagree have been brought into line.
        */}
        <p className="flex items-center gap-2 border-b border-line px-3 py-2 text-label uppercase text-fg-secondary">
          <Mail aria-hidden="true" className="size-3.5" />
          This is exactly what will be sent
        </p>
        <div data-testid="draft-mail" className="flex flex-col gap-2 px-3 py-3">
          <p className="text-body font-medium text-fg">{row.mail.subject}</p>
          {/* `whitespace-pre-wrap`: the body is newline-formatted text, and collapsing it
            * would show the reviewer a letter that is not the one being sent. */}
          <p className="whitespace-pre-wrap text-body13 text-fg-secondary">{row.mail.body}</p>
        </div>
      </section>

      {/*
        Rule 11, on the row: the LAST refusal, in the server's own words, kept until a send
        succeeds. Not a toast — a toast for this would scroll away and leave a draft that
        looks untouched.

        ⚠ IT IS A TINTED BOX AND THE FIRST VERSION WAS NOT — A MEASURED DARK-MODE FAILURE.
        It shipped for ten minutes as bare `text-danger-fg` on the card surface, which is
        the register's own recorded trap: `--crf-status-danger-text` is #ca2525 and is AA
        only against the PALE danger background it is designed to pair with. On the card
        surface it measures 5.52:1 light and **2.98:1 dark**, against a 4.5 floor for
        normal text — a hard failure on the one sentence that explains why a customer was
        not contacted. Paired with `bg-danger-bg` (both variables mode-invariant) it is
        4.52:1 in BOTH modes. `renewals.contrast.test.ts` pins both numbers, so the trap
        is now a failing test rather than a memory.
      */}
      {row.send_error !== null ? (
        <p
          data-testid="draft-send-error"
          className="rounded-sm bg-danger-bg px-3 py-2 text-body13 text-danger-fg"
        >
          <span className="font-medium">Last send failed:</span> {row.send_error}
        </p>
      ) : null}

      {/* The refusal from THIS click, which arrives before the refetch lands. */}
      {sendError !== null ? (
        <Alert tone="danger" title="This reminder was not sent">
          {sendError}
        </Alert>
      ) : null}

      {blocked ? <BlockedReason row={row} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {/*
          ⚠ D5 — ABSENT, NOT DISABLED. A seat without `quotes.send` gets no control here at
          all; the sentence at the top of the screen explains the absence. The `disabled`
          below is only ever a STATE block (already renewed, expiry moved) or a mutation in
          flight, which is the distinction the file header draws.
        */}
        {/*
          THE SHARED `Button` PRIMITIVE AS IT SHIPS, NOT A HAND-ROLLED FILL. Its pair —
          `text/inverse` on `accent/indigo` — measures 4.22:1, which `quotes.contrast.test.ts`
          already records as a SHELL-WIDE finding worn by every primary button in the app.
          Re-implementing the fill here would have made this screen the place that quietly
          authored a sixth button colour, and forking the design system for one control is
          the trade the register already refused for its overdue rule.
        */}
        {canSend ? (
          <Button
            data-testid="send-draft"
            disabled={blocked || sending}
            onClick={onSend}
          >
            <Send aria-hidden="true" className="size-4" />
            {/*
              ⚠ THE ADDRESS IS IN THE LABEL. A button reading "Send" can be pressed by
              reflex; one reading "Send to priya@acme.example" cannot be pressed without
              having read the recipient, which is the review this whole screen exists to
              make real.
            */}
            {sending ? 'Sending…' : `Send to ${row.recipient.email}`}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

/* ───────────────────────────────────────────────── have we sent this before ── */

/**
 * ⚠ THE ANSWER TO "HAVE WE ALREADY EMAILED THIS?", ON THE CARD, INCLUDING WHEN IT IS NO.
 *
 * This is the sentence the whole unit exists for. The doubt occurs here — an operator with a
 * draft in front of them, about to press Send — so the answer is here, at no extra request,
 * because the two scalars ride the query the card already came from.
 *
 * ⚠ THE NEGATIVE IS PRINTED. An absent sentence is not an answer: a reader cannot tell "we
 * have not contacted them" from "this did not load", and the one they act on is the
 * negative. Same rule as `StatusChip`'s "Not sent yet" two blocks down, and the same rule 11
 * that migration 27 was authorised under.
 *
 * ⚠ AND THE WORDING IS "THIS 30-DAY REMINDER", NEVER "THIS CUSTOMER". The count is scoped to
 * this contract line and this window, at a DIFFERENT expiry — see `same_window_sends`. A
 * sentence saying "this customer has had 2 reminders" would be describing a different number
 * from the one being shown, and would read as a warning on the ordinary 60 → 30 → 7 cadence.
 * The link is where the broader question gets answered, filtered to the party.
 */
function PriorSends({
  row,
  onSeeSent,
}: {
  row: RenewalDraftRow;
  onSeeSent: () => void;
}): React.JSX.Element {
  const repeat = row.same_window_sends > 0;
  return (
    <div
      data-testid="prior-sends"
      data-repeat={repeat ? 'yes' : 'no'}
      className={cn(
        'flex flex-wrap items-baseline gap-x-2 rounded-sm px-3 py-2 text-body13',
        /*
         * ⚠ `warn-`, NOT `warning-`. The Tailwind theme maps `--color-warn-fg` /
         * `--color-warn-bg` (app.css:85-86); `bg-warning-bg` matches no token, so Tailwind
         * emits NOTHING for it and the block would have rendered unstyled — a warning that
         * looks like body text, on the one sentence that stops a customer being emailed
         * twice. Caught by reading the theme rather than by looking at it.
         *
         * The pair is the app's own, already pinned at 4.51:1 in BOTH modes by
         * `renewals.contrast.test.ts` (both variables are mode-invariant). The QUIET case is
         * `text-fg-secondary` on the card's `bg-surface` — 4.64:1 light / 5.85:1 dark — and
         * NOT `text-fg-muted`, which is 2.63:1 there and carries no text anywhere in this
         * app. A "nothing has been sent" line is not decoration: it is the sentence the
         * operator acts on.
         */
        repeat ? 'bg-warn-bg text-warn-fg' : 'text-fg-secondary',
      )}
    >
      {repeat ? (
        <span>
          <span className="font-medium">
            This {row.window_days}-day reminder has already been sent to this customer
            {row.same_window_last_sent_at === null
              ? ''
              : ` — most recently ${formatSentAt(row.same_window_last_sent_at)}`}
            .
          </span>{' '}
          The expiry changed afterwards, which prepared this fresh draft. Sending it emails
          them a second {row.window_days}-day notice.
        </span>
      ) : (
        <span>
          No {row.window_days}-day reminder has been sent to this customer for this contract.
        </span>
      )}
      <button
        type="button"
        data-testid="see-sent-for-party"
        onClick={onSeeSent}
        className="font-medium underline underline-offset-2"
      >
        What has been sent to {row.party_name}?
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the statuses ── */

/**
 * ⚠ "NOT SENT YET" IS PRINTED, NOT IMPLIED.
 *
 * Every row in this queue is `state = 'drafted'` — that is the API's predicate — so the
 * absence of a "sent" marker is technically enough information. It is not ENOUGH ON SCREEN:
 * an ambiguous blank where a status belongs is exactly the rule-11 failure migration 27 was
 * authorised to end, and a reader cannot tell "nobody has sent this" from "this column did
 * not load". So the state is words, in every case, and the failed one is red.
 */
function StatusChip({ row }: { row: RenewalDraftRow }): React.JSX.Element {
  const failed = row.send_error !== null;
  return (
    <span
      data-testid="draft-status"
      data-state={failed ? 'send_failed' : 'not_sent'}
      className={cn(
        'inline-flex h-6 items-center rounded-pill px-2 text-body13',
        failed ? 'bg-danger-bg text-danger-fg' : 'bg-subtle text-fg ring-1 ring-line',
      )}
    >
      {failed ? 'Send failed' : 'Not sent yet'}
    </span>
  );
}

/** Which ledger bucket this draft closes — 60 | 30 | 7. */
function WindowPill({ days }: { days: number }): React.JSX.Element {
  return (
    <span
      data-testid="draft-window"
      className="inline-flex h-6 items-center rounded-pill bg-info-bg px-2 text-body13 text-info-fg"
    >
      {days}-day notice
    </span>
  );
}

/**
 * The three refusals the server will make, stated before the click.
 *
 * ⚠ `superseded` IS NOT AN ERROR AND IS NOT PAINTED AS ONE. The customer already renewed —
 * that is the sale working. The register makes the same choice for the same reason: painting
 * good news red sends somebody to chase a customer who has already paid.
 *
 * ⚠ THE SIGN-OFF ARM NAMES A ROLE AND A COLUMN, AND BOTH ARE MEASURED. `config.manage` is
 * held by **Country Manager** alone in `seeds/role-matrix.json` — there is no "Country
 * Admin" role — and two of the three roles that hold `quotes.send` and therefore see this
 * card (Coordinator, Tally Sales) cannot fix it, so the sentence never says "you". It
 * names no SCREEN because there is none: the admin nav is a closed list of six entries
 * (`domain/nav.ts`) and `PATCH /countries/:cc` has no UI in this product. Filed as a
 * finding rather than invented here.
 *
 * ⚠ AND IT SAYS "the country record", NOT "the country". The company name on this very
 * page comes from the hardcoded `COUNTRIES` mirror, so an operator can be looking at a
 * perfectly good entity name in the switcher while the stored row is the problem. Without
 * those words the alert reads as simply wrong.
 *
 * ⚠ NO NEW COLOUR PAIR. `Alert tone="warning"` is `bg-warn-bg` / `text-warn-fg`, already
 * measured at 4.51:1 in BOTH modes and pinned by `renewals.contrast.test.ts` (both
 * variables are mode-invariant).
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠ THE ORDER OF THE THREE ARMS IS THE **ROUTE'S** ORDER, AND IT USED TO BE A DIFFERENT ONE
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `send()` refuses `superseded`, then `stale`, and only then reaches `deliver()` and the
 * sign-off gate. This function tested superseded → legal entity → stale, so a draft that
 * was BOTH stale and unsignable told the operator the wrong thing: it named a Country
 * Manager and a column, when the server would in fact have answered 409 *"the expiry
 * moved"* — a refusal no entity edit can clear. MEASURED with a fixture carrying both
 * flags; no case covered the combination, so nothing failed.
 *
 * The Send control was disabled either way, so no wrong letter could go out. The defect
 * was dispatching somebody to change customer-facing country configuration for nothing —
 * and it contradicted the rule `deliver()`'s own docblock states for the same reason:
 * *"if a draft is both superseded and unsigned, 'this customer has already renewed' is the
 * message that helps, because fixing the entity would not make that letter sendable."*
 * That argument applies to `stale` word for word. Cheapest remedy last, on BOTH screens.
 */
function BlockedReason({ row }: { row: RenewalDraftRow }): React.JSX.Element {
  if (row.superseded) {
    return (
      <Alert tone="info" title="Already renewed — this will not be sent">
        A later contract for the same product supersedes this one. Chasing the customer now
        would contradict the sale we have just made.
      </Alert>
    );
  }
  if (row.stale) {
    return (
      <Alert tone="warning" title="The expiry moved after this draft was prepared">
        This letter states {formatDateOnly(row.drafted_for_expiry)} and the contract now expires{' '}
        {formatDateOnly(row.expiry_date)}. Editing an expiry re-arms the reminder windows, so the
        next scan prepares a fresh draft carrying the right date. This one must not go out.
      </Alert>
    );
  }
  return (
    <Alert
      tone="warning"
      title={
        row.legal_entity_defect === 'multiline'
          ? 'This country’s legal entity name is more than one line'
          : 'This country has no confirmed legal entity name'
      }
    >
      <span data-testid="blocked-legal-entity">
        {row.legal_entity_defect === 'multiline'
          ? 'The reminder is signed off with the legal entity name and nothing else, so a ' +
            'name carrying an address or a registration number cannot be used. '
          : 'The reminder is signed off with the country’s legal entity name, and the ' +
            'country record does not hold one. It will not be sent unsigned. '}
        The draft stays in the queue. A Country Manager must correct the legal entity name on
        the country record (countries.legal_entity) before this can go out.
      </span>
    </Alert>
  );
}
